import { CSSProperties } from "react";
import {
  PartialBrandTheme,
  PartialQuestThemeData,
  TutorialStyleConfig,
  TutorialThemeConfig,
  DEFAULT_BRAND_THEME,
  DEFAULT_QUEST_THEME,
} from "./tutorial.types";
import { hexToGlassColor } from "../../../../general";

class TutorialThemeManager {
  themeConfig: TutorialThemeConfig;
  styleConfig: TutorialStyleConfig;
  questThemeData: PartialQuestThemeData;
  brandTheme: PartialBrandTheme;

  constructor(
    themeConfig: TutorialThemeConfig = {},
    styleConfig: TutorialStyleConfig = {},
    questThemeData: PartialQuestThemeData = DEFAULT_QUEST_THEME,
    brandTheme: PartialBrandTheme = DEFAULT_BRAND_THEME
  ) {
    this.themeConfig = themeConfig;
    this.styleConfig = styleConfig;
    this.questThemeData = { ...DEFAULT_QUEST_THEME, ...questThemeData };
    this.brandTheme = { ...DEFAULT_BRAND_THEME, ...brandTheme };
  }

  getStyleValueAsString(path: string, defaultValue: string = ""): string {
    const keys = path.split(".");
    let value: any = this.styleConfig;

    for (const key of keys) {
      if (value && typeof value === "object") {
        value = value[key];
      } else {
        return defaultValue;
      }
    }

    return typeof value === "string" ? value : defaultValue;
  }

  getStyleValueAsObject<T>(path: string, defaultValue: T): T {
    const keys = path.split(".");
    let value: any = this.styleConfig;

    for (const key of keys) {
      if (value && typeof value === "object") {
        value = value[key];
      } else {
        return defaultValue;
      }
    }

    return (typeof value === "object" ? value : defaultValue) as T;
  }

  getContainerStyles(): CSSProperties {
    return {
      background: this.getBackgroundColor(),
      height: this.getStyleValueAsString("Form.height", "auto"),
      borderRadius: this.getBorderRadius(),
      fontFamily: this.getFontFamily(),
      ...this.styleConfig?.Form,
    };
  }

  getTopBarStyles() {
    return {
      headingStyle: {
        color:
          this.getStyleValueAsString("Heading.color") ||
          this.brandTheme?.titleColor ||
          this.brandTheme?.primaryColor ||
          this.themeConfig?.primaryColor ||
          "#000000",
        ...this.styleConfig?.Heading,
      },
      descriptionStyle: {
        color:
          this.getStyleValueAsString("Description.color") ||
          this.brandTheme?.contentColor ||
          this.themeConfig?.secondaryColor ||
          "#666666",
        ...this.styleConfig?.Description,
      },
      topbarStyle: {
        ...this.styleConfig?.Topbar,
      },
    };
  }

  getStepStyles() {
    return {
      step: {
        color:
          this.getStyleValueAsString("Description.color") ||
          this.brandTheme?.secondaryColor ||
          this.themeConfig?.secondaryColor ||
          "#666666",
      },
      title: {
        color:
          this.getStyleValueAsString("Heading.color") ||
          this.brandTheme?.titleColor ||
          this.themeConfig?.primaryColor ||
          "#000000",
      },
      description: {
        color:
          this.getStyleValueAsString("Description.color") ||
          this.brandTheme?.contentColor ||
          this.themeConfig?.secondaryColor ||
          "#666666",
      },
    };
  }

  ensureBoolean(value: boolean | undefined): boolean {
    return Boolean(value);
  }

  getProgressStyles(isCompleted: boolean | undefined) {
    const completed = this.ensureBoolean(isCompleted);
    const backgroundColor = this.getBackgroundColor();

    const isLightColor = (hex: string) => {
      if (!hex) return true;
      let c = hex.replace('#', '');
      if (c?.length === 3) {
        c = c + c ;
      }
      const r = parseInt(c.substring(0, 2), 16);
      const g = parseInt(c.substring(2, 4), 16);
      const b = parseInt(c.substring(4, 6), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 180;
    };
    const color = isLightColor(backgroundColor || '#FFFFFF') ? hexToGlassColor('#000000', 0.2) : hexToGlassColor('#FFFFFF', 0.5);

    return {
      imageCont: {
        background: completed
          ? "#01ff0111"
          : (backgroundColor || "#FBFBFB"),
      },
      connector: {
        background: completed
          ? "#73DCA7"
          : (backgroundColor ? color : "#EFEFEF"),
      },
    };
  }

  getFooterStyles() {
    const footerStyle = this.styleConfig?.Footer || {};

    return {
      container: {
        background: this.getBackgroundColor(),
        borderRadius: this.styleConfig?.Form?.borderRadius || `0px 0px ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"} ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"}`,
        ...footerStyle?.FooterStyle,
      },
      text: footerStyle?.FooterTextStyle || {},
      icon: footerStyle?.FooterIcon || {},
      content: {
        text: footerStyle?.FooterText || "",
        link: footerStyle?.FooterLink || "",
      },
    };
  }

  getBackgroundColor(): string {
    return (
      this.getStyleValueAsString("Form.backgroundColor") ||
      this.getStyleValueAsString("Form.background") ||
      this.brandTheme?.background ||
      this.themeConfig?.backgroundColor
    );
  }

  getFontFamily(): string {
    return (
      this.brandTheme?.fontFamily ||
      this.themeConfig?.fontFamily ||
      "'Figtree', sans-serif"
    );
  }

  getBorderRadius(): string {
    return (
      this.getStyleValueAsString("Form.borderRadius") ||
      this.questThemeData?.borderRadius ||
      this.brandTheme?.borderRadius ||
      "8px"
    );
  }

  // Common style getters
  getPrimaryColor(): string {
    return (
      this.getStyleValueAsString("Heading.color") ||
      this.brandTheme?.primaryColor ||
      this.themeConfig?.primaryColor ||
      "#000000"
    );
  }

  getSecondaryColor(): string {
    return (
      this.getStyleValueAsString("Description.color") ||
      this.brandTheme?.secondaryColor ||
      this.themeConfig?.secondaryColor ||
      "#666666"
    );
  }

  // Update methods
  updateThemeConfig(newConfig: Partial<TutorialThemeConfig>): void {
    this.themeConfig = { ...this.themeConfig, ...newConfig };
  }

  updateStyleConfig(newConfig: Partial<TutorialStyleConfig>): void {
    this.styleConfig = {
      ...this.styleConfig,
      ...newConfig,
      Footer: {
        ...this.styleConfig?.Footer,
        ...newConfig?.Footer,
      },
    };
  }

  updateQuestTheme(newTheme: PartialQuestThemeData): void {
    this.questThemeData = {
      ...this.questThemeData,
      ...newTheme,
    };
  }

  updateBrandTheme(newTheme: PartialBrandTheme): void {
    this.brandTheme = {
      ...this.brandTheme,
      ...newTheme,
    };
  }

  mergeStyles(
    baseStyles: CSSProperties,
    overrideStyles?: CSSProperties
  ): CSSProperties {
    return {
      ...baseStyles,
      ...(overrideStyles || {}),
    };
  }

  getComputedStyle(
    path: string,
    defaultStyles: CSSProperties = {}
  ): CSSProperties {
    return this.mergeStyles(
      defaultStyles,
      this.getStyleValueAsObject(path, {})
    );
  }
}

export default TutorialThemeManager;
